/**
 * De-duplication for the `purchase` analytics event.
 *
 * The booking confirmation page is a normal, revisitable URL: a refresh, a back-navigation, or
 * re-opening the link from the confirmation email all remount the page with the same transaction
 * id. An in-component guard (a ref) only survives while that mount does, so every one of those
 * re-entries pushed a second `purchase` and inflated the operator's conversion count.
 *
 * The marker therefore has to outlive the component, the page load and the tab, which is what
 * `localStorage` is for. It holds a bounded, most-recent-first list of transaction ids rather
 * than one key per transaction, so it cannot grow without limit on a shared or staff machine.
 *
 * Note the deliberate difference from `funnelDedupe.ts`, which covers the earlier funnel steps:
 * that one uses `sessionStorage`, because a new tab is a new checkout and should report its
 * funnel again. A `purchase` must never repeat, in any tab, so this one outlives the session.
 *
 * Storage can be unavailable or throw (Safari private browsing, a browser configured to block
 * site data). Every access is guarded by `analyticsStorage`, and a failure degrades to "not yet
 * tracked" — firing a possible duplicate is a better outcome than silently dropping the
 * conversion entirely. Callers should keep their in-memory guard as the first line of defence for
 * that case.
 */

import { readRaw, writeRaw } from './analyticsStorage';

const STORAGE_KEY = 'fh-analytics-tracked-purchases';

/**
 * How many transaction ids to remember. Only needs to cover a single browsing session's worth of
 * revisits; the list is a duplicate guard, not an audit trail.
 */
const MAX_TRACKED = 25;

function readTracked(): string[] {
	const raw = readRaw('localStorage', STORAGE_KEY);
	if (!raw) {
		return [];
	}
	try {
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
	} catch {
		return [];
	}
}

/** True when a `purchase` has already been pushed for this transaction on this browser. */
export function purchaseAlreadyTracked(transactionId: string): boolean {
	if (!transactionId) {
		return false;
	}
	return readTracked().includes(transactionId);
}

/** Record that a `purchase` has been pushed for this transaction, so a revisit will not repeat it. */
export function markPurchaseTracked(transactionId: string): void {
	if (!transactionId) {
		return;
	}
	const next = [transactionId, ...readTracked().filter(x => x !== transactionId)].slice(0, MAX_TRACKED);
	writeRaw('localStorage', STORAGE_KEY, JSON.stringify(next));
}
